import { useQuery } from 'react-query';
import { downloadContent } from '@sgdocs/client';
import { getPermalink } from '@sgdocs/client';
import { getToken } from './getToken';

type TechnicalParams = {
  sgmDocsBaseUrl: string;
  token: string | null;
  enableQuery?: boolean;
  component: string;
};

type FetchDocumentContentParams = {
  workspaceId: string;
  documentId: string;
  versionId?: string;
  mimeType: string;
};

type FetchPermalinkContentParams = { permalinkId: string };

type ApiParams = FetchDocumentContentParams | FetchPermalinkContentParams;

type BlobWithInfo = {
  rawContent: string;
  contentBlob: Blob;
  contentType: string | null;
  documentName: string;
};

const isPermalinkParams = (params: ApiParams): params is FetchPermalinkContentParams =>
  (params as FetchPermalinkContentParams).permalinkId !== undefined;

const getFilenameFromContentDisposition = (response: Response) => {
  const contentDisposition = response.headers.get('content-disposition');
  let filename = '';
  if (contentDisposition) {
    const strings = contentDisposition.split(';').find((n) => n.includes('filename='));
    filename = strings ? strings.replace('filename=', '').replace(new RegExp('"', 'g'), '').trim() : '';
  }
  return filename;
};

const retrieveBlobWithInfo = async (response: Response): Promise<BlobWithInfo> => {
  const contentBlob = await response.blob();
  const rawContent = await contentBlob.text();
  return {
    rawContent,
    contentBlob,
    contentType: response.headers.get('Content-Type'),
    documentName: getFilenameFromContentDisposition(response),
  };
};

const queryDocumentContent = (
  { workspaceId, documentId, versionId, mimeType }: FetchDocumentContentParams,
  { token, sgmDocsBaseUrl, component }: TechnicalParams
): Promise<BlobWithInfo> => {
  return downloadContent({
    workspaceId,
    documentId,
    versionId,
    mimeType,
    authorizationHeader: token,
    baseUrl: sgmDocsBaseUrl,
    component
  }).then(retrieveBlobWithInfo);
};

const queryPermalinkContent = (
  { permalinkId }: FetchPermalinkContentParams,
  { token, sgmDocsBaseUrl, component }: TechnicalParams
): Promise<BlobWithInfo> => {
  return getPermalink({
    permalinkId,
    authorizationHeader: token,
    baseUrl: sgmDocsBaseUrl,
    component
  }).then(retrieveBlobWithInfo);
};

const queryContent = (params: ApiParams, tp: TechnicalParams): Promise<BlobWithInfo> =>
  isPermalinkParams(params) ? queryPermalinkContent(params, tp) : queryDocumentContent(params, tp);

export const useQueryFetchContent = (params: ApiParams, { sgmDocsBaseUrl, token, enableQuery, component }: TechnicalParams) => {
  const tokenUsed = getToken(token);

  const queryKey = isPermalinkParams(params)
    ? `content-permalink-${params.permalinkId}`
    : `content-workspace-${params.workspaceId}-document-${params.documentId}-version-${params.versionId}-mimetype-${params.mimeType}`;

  return useQuery(queryKey, () => queryContent(params, { token: tokenUsed, sgmDocsBaseUrl, component }), {
    refetchOnWindowFocus: false,
    enabled: Boolean(tokenUsed) && (enableQuery !== undefined ? enableQuery : true),
  });
};
